import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { create } from 'zustand';

export interface MenuItem {
  label: string;
  shortcut?: string;
  /** Longer explanation shown as tooltip (defaults to the label). */
  description?: string;
  disabled?: boolean;
  danger?: boolean;
  onSelect(): void;
}
export type MenuEntry = MenuItem | 'separator';

interface MenuState {
  open: { x: number; y: number; items: MenuEntry[] } | null;
}

const useMenu = create<MenuState>()(() => ({ open: null }));

export function openContextMenu(x: number, y: number, items: MenuEntry[]): void {
  useMenu.setState({ open: { x, y, items } });
}

export function ContextMenuHost() {
  const open = useMenu((s) => s.open);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({
      x: Math.min(open.x, window.innerWidth - r.width - 8),
      y: Math.min(open.y, window.innerHeight - r.height - 8),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof PointerEvent && ref.current?.contains(e.target as Node)) return;
      useMenu.setState({ open: null });
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && useMenu.setState({ open: null });
    window.addEventListener('pointerdown', close, true);
    window.addEventListener('blur', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', close, true);
      window.removeEventListener('blur', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!open) return null;
  return (
    <div ref={ref} className="context-menu" style={{ left: pos.x, top: pos.y }} role="menu">
      {open.items.map((item, i) =>
        item === 'separator' ? (
          <div key={i} className="context-sep" />
        ) : (
          <button
            key={i}
            role="menuitem"
            className={`context-item${item.danger ? ' danger' : ''}`}
            data-tooltip={item.description ?? item.label}
            data-tooltip-shortcut={item.shortcut}
            disabled={item.disabled}
            onClick={() => {
              useMenu.setState({ open: null });
              item.onSelect();
            }}
          >
            <span>{item.label}</span>
            {item.shortcut && <span className="muted">{item.shortcut}</span>}
          </button>
        ),
      )}
    </div>
  );
}
